import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Bell, Globe, Lock, Mail, MapPin, Plus, Pencil, Trash2, User, Loader2, AlertCircle, ExternalLink, CheckCircle, Check, Crown, CreditCard, Calendar, Package, Settings as SettingsIcon, Gift, Tag, Zap, Clock, ArrowRight, XCircle, AlertTriangle, RefreshCw, UserX } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { AddressElement } from "@stripe/react-stripe-js";
import { useToast } from "@/hooks/use-toast";
import { useAuthContext } from "@/lib/AuthProvider";
import { Address, NotificationPreferences } from "@/services/userProfileService";
import { useUserProfile } from "@/context/UserProfileContext";
import { useRushdMembership, useRushdMembershipStatus } from "@/context/RushdMembershipContext";
import { useCurrency } from "@/context/CurrencyContext";
import { RUSHD_PLAN_CONFIG, RUSHD_PERKS } from "@/types/rushd";
import { getSettingsSEO, validateSEOData, generateKeywords } from "@/services/seoService";
import { Link } from "react-router-dom";

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentAddress, setCurrentAddress] = useState<Address | null>(null);
  const { toast } = useToast();
  const { user, isGoogleUser, changePassword, deleteAccount } = useAuthContext();
  const { userProfile, loading, addUserAddress, updateUserAddress, deleteUserAddress, updateUserProfileInfo, updateUserNotificationPreferences, refreshProfile } = useUserProfile();
  
  // Rush'd contexts
  const { createBillingPortalSession, refreshMembership } = useRushdMembership();
  const { isMember, isActive, membershipStatus, loading: loadingRushd } = useRushdMembershipStatus();
  const { formatDisplayAmount, getConvertedItemPrice } = useCurrency();
  
  // Get tab from URL parameter, default to "account"
  const tabFromUrl = searchParams.get('tab') || 'account';
  const validTabs = ['account', 'addresses', 'security', 'notifications', 'rushd'];
  const defaultTab = validTabs.includes(tabFromUrl) ? tabFromUrl : 'account';

  // Address form state
  const [addressLabel, setAddressLabel] = useState("");
  const [addressDetails, setAddressDetails] = useState<any>(null);
  const [isDefaultAddress, setIsDefaultAddress] = useState(false);
  const [addressComplete, setAddressComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetKey, setResetKey] = useState(Date.now()); // Used to force re-render of AddressElement

  // Dialog state
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountConfirmation, setDeleteAccountConfirmation] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  
  // Account tab state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    orderUpdates: false,
    promotions: false,
    newsletters: false,
  });
  const [originalNotificationPreferences, setOriginalNotificationPreferences] = useState({
    orderUpdates: false,
    promotions: false,
    newsletters: false,
  });
  const [isUpdatingNotifications, setIsUpdatingNotifications] = useState(false);
  const [showNotificationConfirmDialog, setShowNotificationConfirmDialog] = useState(false);

  // Rush'd state
  const [loadingBillingPortal, setLoadingBillingPortal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Profile refresh tracking using ref instead of state
  const profileRefreshed = React.useRef(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Extract first and last name when profile loads
  useEffect(() => {
    if (userProfile) {
      const nameParts = userProfile.name.split(' ');
      const first = nameParts[0] || '';
      // Join the rest of the array as the last name (handles middle names too)
      const last = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      setFirstName(first);
      setLastName(last);
      setOriginalName(userProfile.name);
    }
  }, [userProfile]);

  // Sync notification preferences when profile loads
  useEffect(() => {
    if (userProfile?.notificationPreferences) {
      setNotificationPreferences(userProfile.notificationPreferences);
      setOriginalNotificationPreferences(userProfile.notificationPreferences);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!deleteAccountDialogOpen) {
      setDeleteAccountConfirmation("");
    }
  }, [deleteAccountDialogOpen]);

  // Refresh user profile when addresses tab is selected to ensure latest saved addresses
  const handleTabChange = async (tabValue: string) => {
    // Update URL parameter
    setSearchParams({ tab: tabValue });
    
    if (tabValue === "addresses" && user && refreshProfile && !profileRefreshed.current) {
      profileRefreshed.current = true;
      try {
        if (import.meta.env.DEV) {
          console.log("Refreshing user profile to ensure latest addresses...");
        }
        await refreshProfile();
      } catch (error) {
        console.error("Error refreshing user profile:", error);
        // Don't show error to user as this is a background refresh
      }
    }
  };

  // Check if name has been changed
  const hasNameChanged = () => {
    const currentFullName = `${firstName} ${lastName}`.trim();
    return currentFullName !== originalName;
  };

  // Handle saving profile changes
  const handleSaveProfile = async () => {
    if (!user) return;
    
    try {
      setIsUpdatingProfile(true);
      const fullName = `${firstName} ${lastName}`.trim();
      
      await updateUserProfileInfo({ name: fullName });
      
      setOriginalName(fullName);
      
      toast({
        title: "Profile Updated",
        description: "Your profile information has been updated successfully.",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle the address element change event
  const handleAddressChange = (event: any) => {
    setAddressDetails(event);
    setAddressComplete(event.complete);
  };

  // Open dialog for adding a new address
  const handleAddAddress = () => {
    setAddressLabel("");
    setAddressDetails(null);
    setIsDefaultAddress(false);
    setAddressComplete(false);
    setIsEditing(false);
    setCurrentAddress(null);
    setResetKey(Date.now()); // Generate a new key to force AddressElement to reset
    setAddressDialogOpen(true);
  };

  // Open dialog for editing an address
  const handleEditAddress = (address: Address) => {
    setCurrentAddress(address);
    setAddressLabel(address.label);
    setIsDefaultAddress(address.isDefault);
    setIsEditing(true);
    setAddressDialogOpen(true);
  };

  // Open confirmation dialog for deleting an address
  const handleDeleteConfirmation = (address: Address) => {
    setAddressToDelete(address);
    setDeleteDialogOpen(true);
  };

  // Save new or updated address
  const handleSaveAddress = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to manage your addresses.",
        variant: "destructive",
      });
      return;
    }
    
    if (!addressComplete) {
      toast({
        title: "Address Incomplete",
        description: "Please fill out all required address fields.",
        variant: "destructive",
      });
      return;
    }

    if (!addressLabel.trim()) {
      toast({
        title: "Address Label Required",
        description: "Please provide a label for this address (e.g., Home, Work).",
        variant: "destructive",
      });
      return;
      }

    try {
      setIsSubmitting(true);
      
      const addressValue = addressDetails.value;
      const formattedAddress = {
        label: addressLabel,
        fullName: addressValue.name,
        line1: addressValue.address.line1,
        line2: addressValue.address.line2 || "",
        city: addressValue.address.city,
        state: addressValue.address.state,
        zipCode: addressValue.address.postal_code,
        country: addressValue.address.country,
        phoneNumber: addressValue.phone || "",
        isDefault: isDefaultAddress,
      };

      if (isEditing && currentAddress) {
        // Update existing address 
        await updateUserAddress({
          ...formattedAddress,
          id: currentAddress.id 
        });
        
        toast({
          title: "Address Updated",
          description: "Your address has been updated successfully.",
        });
      } else {
        // Add new address
        await addUserAddress(formattedAddress);
        
        toast({
          title: "Address Added",
          description: "Your new address has been added successfully.",
        });
      }
      
    setAddressDialogOpen(false);
    } catch (error) {
      console.error("Error saving address:", error);
      toast({
        title: "Error",
        description: "Failed to save address. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete an address after confirmation
  const handleDeleteAddress = async () => {
    if (!addressToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteUserAddress(addressToDelete.id);
      
      toast({
        title: "Address deleted",
        description: "Your address has been successfully deleted.",
      });
      
      setDeleteDialogOpen(false);
      setAddressToDelete(null);
    } catch (error) {
      console.error('Error deleting address:', error);
      toast({
        title: "Error",
        description: "Failed to delete address. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDeleteAccount = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in again to delete your account.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDeletingAccount(true);
      await deleteAccount();
      toast({
        title: "Account deleted",
        description: "Your JamPack'd account has been permanently removed.",
      });
      if (isMountedRef.current) {
        setDeleteAccountDialogOpen(false);
        setDeleteAccountConfirmation("");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({
        title: "Unable to delete account",
        description: error instanceof Error ? error.message : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      if (isMountedRef.current) {
        setIsDeletingAccount(false);
      }
    }
  };

  // Password validation function (same as AuthModal)
  const validatePassword = (password: string) => {
    const requirements = {
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    
    return requirements;
  };

  const passwordRequirements = validatePassword(newPassword);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  // Validate password confirmation
  useEffect(() => {
    if (confirmNewPassword && newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  }, [newPassword, confirmNewPassword]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }

    if (!isPasswordValid) {
      setPasswordError("Password does not meet all requirements");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password");
      return;
    }

    setIsChangingPassword(true);
    setPasswordError("");

    try {
      await changePassword(currentPassword, newPassword);
      
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      
      toast({
        title: "Password Changed",
        description: "Your password has been successfully updated.",
      });
    } catch (error: any) {
      console.error('Password change error:', error);
      
      // Handle specific Firebase error codes
      switch (error.code) {
        case 'auth/wrong-password':
          setPasswordError("Current password is incorrect");
          break;
        case 'auth/weak-password':
          setPasswordError("New password is too weak");
          break;
        case 'auth/requires-recent-login':
          setPasswordError("Please sign out and sign back in, then try again");
          break;
        default:
          setPasswordError("Failed to change password. Please try again.");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Handle notification preference updates
  const handleNotificationPreferenceChange = (key: keyof typeof notificationPreferences, value: boolean) => {
    if (!user) return;
    
    const updatedPreferences = {
      ...notificationPreferences,
      [key]: value,
    };
    
    // Update local state immediately for better UX
    setNotificationPreferences(updatedPreferences);
  };

  // Handle notification preference local updates (no Firebase call)
  const handleNotificationPreferenceChangeLocal = (key: keyof typeof notificationPreferences, value: boolean) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Check if notification preferences have changed
  const hasNotificationPreferencesChanged = () => {
    return JSON.stringify(notificationPreferences) !== JSON.stringify(originalNotificationPreferences);
  };

  // Handle saving notification preferences with confirmation
  const handleSaveNotificationPreferences = () => {
    setShowNotificationConfirmDialog(true);
  };

  // Confirm and save notification preferences
  const confirmSaveNotificationPreferences = async () => {
    if (!user) return;
    
    try {
      setIsUpdatingNotifications(true);
      setShowNotificationConfirmDialog(false);
      
      await updateUserNotificationPreferences(notificationPreferences);
      
      // Update the original preferences to reflect the saved state
      setOriginalNotificationPreferences(notificationPreferences);
      
      toast({
        title: "Preferences Saved",
        description: "Your notification preferences have been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving notification preferences:", error);
      toast({
        title: "Error",
        description: "Failed to save notification preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingNotifications(false);
    }
  };

  // Reset notification preferences to original values
  const resetNotificationPreferences = () => {
    setNotificationPreferences(originalNotificationPreferences);
    setShowNotificationConfirmDialog(false);
  };

  // Rush'd functions
  const handleBillingPortal = async () => {
    if (!user?.uid || !isMember) return;
    
    setLoadingBillingPortal(true);
    
    try {
      const currentUrl = window.location.origin;
      const returnUrl = `${currentUrl}/settings?tab=rushd`;
      
      const session = await createBillingPortalSession(returnUrl);
      
      // Open billing portal in the same tab
      window.location.href = session.url;
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast({
        title: 'Portal Error',
        description: 'Unable to open billing portal. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoadingBillingPortal(false);
    }
  };
  
  const handleRefreshMembership = async () => {
    setRefreshing(true);
    try {
      await refreshMembership();
      toast({
        title: 'Refreshed',
        description: 'Membership status updated.',
        variant: 'default',
      });
    } catch (error) {
      toast({
        title: 'Refresh Failed',
        description: 'Unable to refresh membership status.',
        variant: 'destructive',
      });
    } finally {
      setRefreshing(false);
    }
  };
  
  const getStatusColor = () => {
    if (!isMember) return 'bg-gray-100 text-gray-700';
    if (isActive) return 'bg-green-100 text-green-700';
    return 'bg-red-100 text-red-700';
  };
  
  const getStatusIcon = () => {
    if (!isMember) return <XCircle className="w-4 h-4" />;
    if (isActive) return <CheckCircle className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };
  
  const getStatusText = () => {
    if (!isMember) return 'Not a Member';
    if (isActive) return 'Active Member';
    if (membershipStatus?.cancelAtPeriodEnd) return 'Cancelling';
    return 'Inactive';
  };
  
  const formatPlanName = (plan?: string) => {
    if (!plan) return '';
    return plan === 'yearly' ? 'Yearly Plan' : 'Monthly Plan';
  };
  
  const getUsagePercentage = () => {
    if (!membershipStatus?.usageLimit || !membershipStatus?.usageThisPeriod) return 0;
    return (membershipStatus.usageThisPeriod / membershipStatus.usageLimit) * 100;
  };

  const addresses = userProfile?.addresses || [];

  const GoogleUserAccountCard = () => (
    <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-white to-gray-50/50">
      {/* Header with gradient background */}
      <CardHeader className="relative pb-6 border-b-0 bg-gradient-to-r from-jamaica-green to-jamaica-green/80 text-white">
        {/* Background pattern overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-xl font-semibold">Personal Information</CardTitle>
            <CardDescription className="text-white/90 text-sm">
              Managed by your Google account
            </CardDescription>
          </div>
          {/* Enhanced Google logo with better styling */}
          <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-white backdrop-blur-sm border border-white/30 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Info banner with improved styling */}
        <div className="p-6 pb-4">
          <div className="relative rounded-xl p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/50 shadow-sm">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-transparent rounded-bl-full"></div>
            <div className="relative flex items-start">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-500/10 border border-blue-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </div>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">Google Account Integration</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Your profile information is securely linked to your Google account. 
                  To modify your personal details, please use Google's account management portal.
              </p>
              </div>
            </div>
            </div>
          </div>
          
        {/* Profile information with enhanced styling */}
        <div className="p-6 pt-4">
          <div className="space-y-4">
            <div className="group p-4 rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50/50 to-white hover:shadow-md hover:border-gray-200 transition-all duration-200">
              <div className="flex items-center">
                <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200/50 border border-gray-200/50 mr-4 group-hover:shadow-sm transition-shadow">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Full Name</p>
                  <p className="font-semibold text-gray-900 truncate">{userProfile?.name || 'Not available'}</p>
                </div>
              </div>
            </div>

            <div className="group p-4 rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50/50 to-white hover:shadow-md hover:border-gray-200 transition-all duration-200">
              <div className="flex items-center">
                <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200/50 border border-gray-200/50 mr-4 group-hover:shadow-sm transition-shadow">
                  <Mail className="h-5 w-5 text-gray-600" />
              </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Email Address</p>
                    <div className="flex items-center">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-green-600 font-medium ml-1">Verified</span>
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900 truncate">{userProfile?.email || 'Not available'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action button with enhanced styling */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 p-6 border-t border-gray-100">
          <a 
            href="https://myaccount.google.com/personal-info" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="group w-full inline-flex items-center justify-center px-6 py-3.5 rounded-xl border border-transparent bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white font-medium hover:from-jamaica-green/90 hover:to-jamaica-green hover:shadow-lg hover:shadow-jamaica-green/25 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-jamaica-green focus:ring-offset-2"
          >
            <span>Manage Google Account</span>
            <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-200" />
          </a>
        </div>
      </CardContent>
    </Card>
  );

  const GoogleUserSecurityCard = () => (
    <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-white to-gray-50/50">
      {/* Header with gradient background */}
      <CardHeader className="relative pb-6 border-b-0 bg-gradient-to-r from-jamaica-green to-jamaica-green/80 text-white">
        {/* Background pattern overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-xl font-semibold">Security Settings</CardTitle>
            <CardDescription className="text-white/90 text-sm">
              Managed by your Google account
            </CardDescription>
          </div>
          {/* Enhanced Google logo with better styling */}
          <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-white backdrop-blur-sm border border-white/30 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Info banner with improved styling */}
        <div className="p-6 pb-4">
          <div className="relative rounded-xl p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100/50 shadow-sm">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-transparent rounded-bl-full"></div>
            <div className="relative flex items-start">
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-500/10 border border-blue-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
              </div>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">Google Account Security</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Your security settings including password, two-factor authentication, and account recovery options are managed through your Google account.
              </p>
              </div>
            </div>
            </div>
          </div>
          
        {/* Security features with enhanced styling */}
        <div className="p-6 pt-4">
          <div className="space-y-4">
            <div className="group p-4 rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50/50 to-white hover:shadow-md hover:border-gray-200 transition-all duration-200">
              <div className="flex items-center">
                <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200/50 border border-gray-200/50 mr-4 group-hover:shadow-sm transition-shadow">
                  <Lock className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Password Management</p>
                  <p className="font-semibold text-gray-900 truncate">Managed by Google</p>
                  <p className="text-xs text-gray-500 mt-1">Change your password through Google Account settings</p>
                </div>
              </div>
            </div>

            <div className="group p-4 rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50/50 to-white hover:shadow-md hover:border-gray-200 transition-all duration-200">
              <div className="flex items-center">
                <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200/50 border border-gray-200/50 mr-4 group-hover:shadow-sm transition-shadow">
                  <AlertCircle className="h-5 w-5 text-gray-600" />
              </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Account Recovery</p>
                  <p className="font-semibold text-gray-900 truncate">Google Account Recovery</p>
                  <p className="text-xs text-gray-500 mt-1">Manage recovery options and backup codes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action button with enhanced styling */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 p-6 border-t border-gray-100">
          <a 
            href="https://myaccount.google.com/security" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="group w-full inline-flex items-center justify-center px-6 py-3.5 rounded-xl border border-transparent bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white font-medium hover:from-jamaica-green/90 hover:to-jamaica-green hover:shadow-lg hover:shadow-jamaica-green/25 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-jamaica-green focus:ring-offset-2"
          >
            <span>Manage Google Security</span>
            <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-200" />
          </a>
        </div>
      </CardContent>
    </Card>
  );

  // Generate SEO data for Settings page
  const seoData = validateSEOData(getSettingsSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={seoData.url} />
      </Helmet>
      
      <div className="flex flex-col min-h-screen">
      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Diagonal Lines Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(45deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px),
                linear-gradient(-45deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px, 120px 120px',
              backgroundPosition: '0 0, 40px 40px'
            }}></div>
          </div>
          
          {/* Gradient Blobs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-jamaica-green/3 to-jamaica-yellow/3 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-r from-jamaica-yellow/4 to-jamaica-green/3 rounded-full mix-blend-multiply filter blur-2xl animate-pulse delay-1500"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col gap-6">
            {/* Enhanced Hero Section */}
            <div className="mb-12 text-center">
              <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
                <div className="relative">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 sm:hover:-rotate-6 transition-transform duration-500">
                    <User className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                    <span className="text-[10px] sm:text-xs font-bold text-white">⚙️</span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
                  <span className="text-xs sm:text-sm font-bold text-jamaica-black">
                    Account Management
                  </span>
                </div>
              </div>

                          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
              <span className="bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green bg-clip-text text-transparent p-1">
                Settings
              </span>
            </h1>
            <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Manage your account settings and preferences.
            </p>
            <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
              Manage your account settings and preferences.
            </p>
          </div>
        
        <div>
          {/* Main Content */}
          <div className="flex-1">
            <Tabs defaultValue={defaultTab} className="w-full" onValueChange={handleTabChange}>
              <TabsList className="grid w-full md:w-auto md:inline-flex grid-cols-5 mb-8 bg-muted">
                <TabsTrigger value="account" className="flex items-center gap-2 px-4">
                  <User className="h-4 w-4" />
                  <span className="hidden md:inline">Account</span>
                </TabsTrigger>
                <TabsTrigger value="addresses" className="flex items-center gap-2 px-4">
                  <MapPin className="h-4 w-4" />
                  <span className="hidden md:inline">Addresses</span>
                </TabsTrigger>
                <TabsTrigger value="security" className="flex items-center gap-2 px-4">
                  <Lock className="h-4 w-4" />
                  <span className="hidden md:inline">Security</span>
                </TabsTrigger>
                <TabsTrigger value="notifications" className="flex items-center gap-2 px-4">
                  <Bell className="h-4 w-4" />
                  <span className="hidden md:inline">Notifications</span>
                </TabsTrigger>
                <TabsTrigger value="rushd" className="flex items-center gap-2 px-4">
                  <Crown className="h-4 w-4" />
                  <span className="hidden md:inline">Rush'd</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="account" className="space-y-6">
                {isGoogleUser() ? (
                  <GoogleUserAccountCard />
                ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                    <CardDescription>Update your personal information.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First name</Label>
                          <Input id="firstName" placeholder="John" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last name</Label>
                          <Input id="lastName" placeholder="Doe" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                      <Label htmlFor="email">Email</Label>
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-sm text-green-600 font-medium">Verified</span>
                        </div>
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input 
                            id="email" 
                            value={userProfile?.email || ''}
                            placeholder="user@example.com" 
                            className="pl-10" 
                            disabled 
                          />
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                      <Button 
                        className="ml-auto bg-jamaica-green hover:bg-jamaica-green/90" 
                        onClick={handleSaveProfile} 
                        disabled={!hasNameChanged() || isUpdatingProfile}
                      >
                        {isUpdatingProfile ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                  </CardFooter>
                </Card>
                )}
                <Card className="border border-destructive/30 bg-destructive/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-5 w-5" />
                      Delete Account
                    </CardTitle>
                    <CardDescription>
                      Permanently remove your JamPack'd profile, saved addresses, cart, and order history.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm text-muted-foreground">
                    <p>Once your account is deleted, this data can't be restored.</p>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>Your Rush'd membership and benefits will be cancelled.</li>
                      <li>Saved shipping addresses and current cart contents will be removed.</li>
                      <li>Order history and delivery tracking details will be cleared.</li>
                    </ul>
                    {isMember && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                        <AlertTriangle className="mt-0.5 h-4 w-4" />
                        <p>
                          You currently have an active Rush'd membership. We'll cancel it automatically before removing your account so you won't be billed again.
                        </p>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                    <Button
                      variant="destructive"
                      className="w-full sm:w-auto"
                      onClick={() => setDeleteAccountDialogOpen(true)}
                    >
                      <UserX className="mr-2 h-4 w-4" />
                      Delete Account
                    </Button>
                  </CardFooter>
                </Card>
                <Dialog open={deleteAccountDialogOpen} onOpenChange={setDeleteAccountDialogOpen}>
                  <DialogContent className="sm:max-w-md space-y-4">
                    <DialogHeader>
                      <DialogTitle>Delete your account</DialogTitle>
                      <DialogDescription>
                        This will permanently delete your JamPack'd profile, order history, and Rush'd membership.
                      </DialogDescription>
                    </DialogHeader>
                    {isMember && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900 text-sm">
                        <AlertTriangle className="mt-0.5 h-4 w-4" />
                        <p>We'll cancel your Rush'd membership as part of this process so future charges stop immediately.</p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="delete-account-confirmation">Type DELETE to confirm</Label>
                      <Input
                        id="delete-account-confirmation"
                        placeholder="DELETE"
                        value={deleteAccountConfirmation}
                        onChange={(e) => setDeleteAccountConfirmation(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                      <p>This action cannot be undone. You’ll be signed out immediately after your account is removed.</p>
                    </div>
                    <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDeleteAccountDialogOpen(false)}
                        disabled={isDeletingAccount}
                        className="w-full sm:w-auto"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        className="w-full sm:w-auto"
                        disabled={
                          deleteAccountConfirmation.trim().toUpperCase() !== "DELETE" || isDeletingAccount
                        }
                        onClick={handleConfirmDeleteAccount}
                      >
                        {isDeletingAccount ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <UserX className="mr-2 h-4 w-4" />
                            Delete Account
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </TabsContent>
              
              <TabsContent value="addresses" className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <CardTitle>Saved Addresses</CardTitle>
                        <CardDescription>Manage your shipping and billing addresses.</CardDescription>
                      </div>
                      <Button 
                        onClick={handleAddAddress} 
                        className="bg-jamaica-green hover:bg-jamaica-green/90 md:w-auto w-full"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Address
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                        <span className="ml-2 text-gray-600 font-medium">Loading addresses...</span>
                      </div>
                    ) : addresses.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <p>You have no saved addresses. Add an address to get started.</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Label</TableHead>
                                <TableHead>Full Name</TableHead>
                                <TableHead>Address</TableHead>
                                <TableHead>City</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {addresses.map((address) => (
                                <TableRow key={address.id}>
                                  <TableCell className="font-medium">
                                    {address.label}
                                    {address.isDefault && (
                                      <span className="ml-2 text-xs bg-jamaica-green text-white px-2 py-1 rounded-full">
                                        Default
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell>{address.fullName}</TableCell>
                                  <TableCell>{address.line1}</TableCell>
                                  <TableCell>{address.city}</TableCell>
                                  <TableCell>{address.state}</TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex justify-end space-x-2">
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => handleEditAddress(address)}
                                      >
                                        <Pencil className="h-4 w-4" />
                                        <span className="sr-only">Edit</span>
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        className="text-destructive border-destructive hover:bg-destructive/10"
                                        onClick={() => handleDeleteConfirmation(address)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        <span className="sr-only">Delete</span>
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="md:hidden space-y-4">
                          {addresses.map((address) => (
                            <div key={address.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
                              {/* Header with Label and Default Badge */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-gray-900">{address.label}</h3>
                                  {address.isDefault && (
                                    <span className="text-xs bg-jamaica-green text-white px-2 py-1 rounded-full">
                                      Default
                                    </span>
                                  )}
                                </div>
                                <div className="flex space-x-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => handleEditAddress(address)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Pencil className="h-4 w-4" />
                                    <span className="sr-only">Edit</span>
                                  </Button>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="text-destructive border-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                                    onClick={() => handleDeleteConfirmation(address)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    <span className="sr-only">Delete</span>
                                  </Button>
                                </div>
                              </div>

                              {/* Address Details */}
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center">
                                  <User className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                                  <span className="text-gray-900 font-medium">{address.fullName}</span>
                                </div>
                                <div className="flex items-start">
                                  <MapPin className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5" />
                                  <div className="text-gray-600 leading-relaxed">
                                    <div>{address.line1}</div>
                                    {address.line2 && <div>{address.line2}</div>}
                                    <div>{address.city}, {address.state} {address.zipCode}</div>
                                    {address.country && address.country !== 'US' && <div>{address.country}</div>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
                
                {/* Address Dialog */}
                <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
                  <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                      <DialogTitle>{isEditing ? "Edit Address" : "Add New Address"}</DialogTitle>
                      <DialogDescription>
                        {isEditing 
                          ? "Update your address information below." 
                          : "Fill in the details to add a new address."}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2 overflow-y-auto pr-1 my-2 flex-1">
                      <div className="space-y-2">
                        <Label htmlFor="address-label">Address Label</Label>
                        <Input 
                          id="address-label" 
                          placeholder="Home, Work, etc." 
                          value={addressLabel}
                          onChange={(e) => setAddressLabel(e.target.value)}
                          className="focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-gray-300"
                        />
                      </div>
                      
                      {/* Stripe Address Element */}
                      <div className="space-y-2">
                        <Label>Address Details</Label>
                        <div className="border rounded-md p-3">
                          {!isEditing ? (
                            // Use key to force re-render for new addresses
                            <AddressElement
                              key={resetKey}
                              options={{
                                mode: "shipping",
                                fields: {
                                  phone: "always",
                                },
                                validation: {
                                  phone: {
                                    required: "always",
                                  },
                                },
                                // Empty default values for a new address
                                defaultValues: {
                                  name: '',
                                  address: {
                                    line1: '',
                                    line2: '',
                                    city: '',
                                    state: '',
                                    postal_code: '',
                                    country: 'US',
                                  },
                                  phone: '',
                                }
                              }}
                              onChange={handleAddressChange}
                          />
                          ) : (
                            // Normal behavior for editing
                            <AddressElement
                              options={{
                                mode: "shipping",
                                fields: {
                                  phone: "always",
                                },
                                validation: {
                                  phone: {
                                    required: "always",
                                  },
                                },
                                // Prefill with current address data
                                defaultValues: currentAddress ? {
                                  name: currentAddress.fullName,
                                  address: {
                                    line1: currentAddress.line1,
                                    line2: currentAddress.line2 || '',
                                    city: currentAddress.city,
                                    state: currentAddress.state,
                                    postal_code: currentAddress.zipCode,
                                    country: currentAddress.country,
                                  },
                                  phone: currentAddress.phoneNumber || '',
                                } : undefined
                              }}
                              onChange={handleAddressChange}
                          />
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 pt-2">
                        <Switch 
                          id="default-address" 
                          checked={isDefaultAddress}
                          onCheckedChange={setIsDefaultAddress}
                        />
                        <Label htmlFor="default-address">Set as default address</Label>
                      </div>
                    </div>
                    <DialogFooter className="flex-shrink-0">
                      <Button 
                        variant="outline" 
                        onClick={() => setAddressDialogOpen(false)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSaveAddress}
                        className="bg-jamaica-green hover:bg-jamaica-green/90"
                        disabled={!addressComplete || isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {isEditing ? "Updating..." : "Adding..."}
                          </>
                        ) : (
                          isEditing ? "Save Changes" : "Add Address"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader className="flex items-center">
                      <AlertCircle className="h-6 w-6 text-destructive mb-2" />
                      <DialogTitle>Confirm Address Deletion</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to delete this address? This action cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                    
                    {addressToDelete && (
                      <div className="bg-muted p-4 rounded-md mb-4">
                        <div className="font-medium text-sm">{addressToDelete.label}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {addressToDelete.fullName}<br />
                          {addressToDelete.line1}
                          {addressToDelete.line2 && `, ${addressToDelete.line2}`}<br />
                          {addressToDelete.city}, {addressToDelete.state} {addressToDelete.zipCode}<br />
                          {addressToDelete.country}
                        </div>
                      </div>
                    )}
                    
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDeleteAddress}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          "Delete Address"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </TabsContent>
              
              <TabsContent value="security" className="space-y-6">
                {isGoogleUser() ? (
                  <GoogleUserSecurityCard />
                ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Password</CardTitle>
                    <CardDescription>Change your password.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">Current password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input 
                            id="currentPassword" 
                            type="password" 
                            placeholder="••••••••"
                            className="pl-10 py-3"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            disabled={isChangingPassword}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">New password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input 
                            id="newPassword" 
                            type="password" 
                            placeholder="••••••••"
                            className="pl-10 py-3"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={isChangingPassword}
                          />
                        </div>
                        {newPassword && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-md border">
                            <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                            <div className="space-y-1">
                              <div className={`flex items-center text-xs ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                                {passwordRequirements.minLength ? (
                                  <Check className="h-3 w-3 mr-2" />
                                ) : (
                                  <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                                )}
                                At least 6 characters
                              </div>
                              <div className={`flex items-center text-xs ${passwordRequirements.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                                {passwordRequirements.hasUppercase ? (
                                  <Check className="h-3 w-3 mr-2" />
                                ) : (
                                  <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                                )}
                                One uppercase letter (A-Z)
                              </div>
                              <div className={`flex items-center text-xs ${passwordRequirements.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                                {passwordRequirements.hasLowercase ? (
                                  <Check className="h-3 w-3 mr-2" />
                                ) : (
                                  <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                                )}
                                One lowercase letter (a-z)
                              </div>
                              <div className={`flex items-center text-xs ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                                {passwordRequirements.hasNumber ? (
                                  <Check className="h-3 w-3 mr-2" />
                                ) : (
                                  <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                                )}
                                One number (0-9)
                              </div>
                              <div className={`flex items-center text-xs ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
                                {passwordRequirements.hasSpecialChar ? (
                                  <Check className="h-3 w-3 mr-2" />
                                ) : (
                                  <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                                )}
                                One special character (!@#$%^&*)
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmNewPassword">Confirm new password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <Input 
                            id="confirmNewPassword" 
                            type="password" 
                            placeholder="••••••••"
                            className={`pl-10 py-3 ${
                              confirmNewPassword && newPassword !== confirmNewPassword
                                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                                : confirmNewPassword && newPassword === confirmNewPassword
                                ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                                : ""
                            }`}
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            disabled={isChangingPassword}
                          />
                          {confirmNewPassword && (
                            <div className="absolute right-3 top-3">
                              {newPassword === confirmNewPassword ? (
                                <div className="h-5 w-5 text-green-500">
                                  <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              ) : (
                                <AlertCircle className="h-5 w-5 text-red-500" />
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {passwordError && (
                        <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          {passwordError}
                        </div>
                      )}

                      <Button 
                        type="submit"
                        className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white py-3"
                        disabled={isChangingPassword || !currentPassword || !newPassword || !confirmNewPassword || !isPasswordValid || newPassword !== confirmNewPassword}
                      >
                        {isChangingPassword ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Changing Password...
                          </>
                        ) : (
                          "Change Password"
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                )}
                
                {/* 2FA Card - Commented out for now
                <Card>
                  <CardHeader>
                    <CardTitle>Two-Factor Authentication</CardTitle>
                    <CardDescription>Add an extra layer of security to your account.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>SMS Authentication</Label>
                        <p className="text-sm text-muted-foreground">
                          Receive a verification code via SMS.
                        </p>
                      </div>
                      <Switch />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Authenticator App</Label>
                        <p className="text-sm text-muted-foreground">
                          Use an authenticator app to generate codes.
                        </p>
                      </div>
                      <Switch />
                    </div>
                  </CardContent>
                </Card>
                */}
              </TabsContent>
              
              <TabsContent value="notifications" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Email Notifications</CardTitle>
                    <CardDescription>Choose what emails you want to receive.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-jamaica-green" />
                        <span className="ml-2 text-gray-600 font-medium">Loading preferences...</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Order Updates</Label>
                            <p className="text-sm text-muted-foreground">
                              Receive emails about your order status.
                            </p>
                          </div>
                          <Switch 
                            checked={notificationPreferences.orderUpdates}
                            onCheckedChange={(checked) => handleNotificationPreferenceChange('orderUpdates', checked)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Promotions & Discounts</Label>
                            <p className="text-sm text-muted-foreground">
                              Receive special offers and discount codes.
                            </p>
                          </div>
                          <Switch 
                            checked={notificationPreferences.promotions}
                            onCheckedChange={(checked) => handleNotificationPreferenceChange('promotions', checked)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label>Newsletters</Label>
                            <p className="text-sm text-muted-foreground">
                              Receive our monthly newsletter.
                            </p>
                          </div>
                          <Switch 
                            checked={notificationPreferences.newsletters}
                            onCheckedChange={(checked) => handleNotificationPreferenceChange('newsletters', checked)}
                          />
                        </div>
                      </>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="ml-auto bg-jamaica-green hover:bg-jamaica-green/90"
                      onClick={handleSaveNotificationPreferences}
                      disabled={isUpdatingNotifications || loading || !hasNotificationPreferencesChanged()}
                    >
                      {isUpdatingNotifications ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Preferences"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </TabsContent>
              
              <TabsContent value="rushd" className="space-y-6">
                {loadingRushd ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-jamaica-green" />
                      <p className="text-gray-600">Loading membership details...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Membership Status Card */}
                    <Card className="mb-8">
                      <CardHeader>
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <SettingsIcon className="w-5 h-5" />
                            Membership Status
                          </CardTitle>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefreshMembership}
                            disabled={refreshing}
                            className="w-full md:w-auto justify-center"
                          >
                            {refreshing ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Refresh Status
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <div className="flex items-center gap-3 mb-4">
                              <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${getStatusColor()}`}>
                                {getStatusIcon()}
                                {getStatusText()}
                              </div>
                              {membershipStatus?.plan && (
                                <Badge variant="outline">
                                  {formatPlanName(membershipStatus.plan)}
                                </Badge>
                              )}
                            </div>
                            
                            {isMember && membershipStatus && (
                              <div className="space-y-3">
                                {membershipStatus.currentPeriodEnd && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Calendar className="w-4 h-4 text-gray-500" />
                                    <span className="text-gray-600">
                                      Current period ends: 
                                    </span>
                                    <span className="font-medium">
                                      {membershipStatus.currentPeriodEnd.toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                
                                {membershipStatus.nextBillingDate && !membershipStatus.cancelAtPeriodEnd && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <CreditCard className="w-4 h-4 text-gray-500" />
                                    <span className="text-gray-600">
                                      Next billing: 
                                    </span>
                                    <span className="font-medium">
                                      {membershipStatus.nextBillingDate.toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                
                                {membershipStatus.cancelAtPeriodEnd && (
                                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-amber-800">
                                      <AlertTriangle className="w-4 h-4" />
                                      <span className="text-sm font-medium">
                                        Membership will cancel at period end
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {!isMember && (
                              <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-gray-600 mb-3">
                                  You're not currently a Rush'd member. Join today to unlock premium benefits!
                                </p>
                                <Button asChild className="bg-jamaica-green hover:bg-jamaica-green/90">
                                  <Link to="/rushd/pricing">
                                    Join Rush'd
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                  </Link>
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {/* Usage Tracking */}
                          {isMember && isActive && membershipStatus?.usageLimit && (
                            <div>
                              <h3 className="font-medium text-gray-900 mb-3">Usage This Period</h3>
                              <div className="space-y-3">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-600">Express shipping orders</span>
                                  <span className="font-medium">
                                    {membershipStatus.usageThisPeriod || 0} / {membershipStatus.usageLimit}
                                  </span>
                                </div>
                                <Progress value={getUsagePercentage()} className="h-2" />
                                <p className="text-xs text-gray-500">
                                  Resets on {membershipStatus.nextBillingDate?.toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Billing Portal Button */}
                        {isMember && (
                          <div className="mt-6 pt-6 border-t">
                            <Button
                              onClick={handleBillingPortal}
                              disabled={loadingBillingPortal}
                              className="flex items-center gap-2"
                            >
                              {loadingBillingPortal ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <ExternalLink className="w-4 h-4" />
                              )}
                              Manage Billing
                            </Button>
                            <p className="text-xs text-gray-500 mt-2">
                              Update payment method, change plan, or cancel subscription
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Benefits Overview */}
                    {isMember && isActive && (
                      <Card className="mb-8">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Gift className="w-5 h-5" />
                            Your Rush'd Benefits
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-3 gap-6">
                            {RUSHD_PERKS.map((perk) => {
                              const IconComponent = {
                                Package,
                                Tag,
                                Gift,
                              }[perk.icon] || Package;
                              
                              return (
                                <div key={perk.id} className="text-center">
                                  <div className="w-12 h-12 bg-jamaica-green/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                                    <IconComponent className="w-6 h-6 text-jamaica-green" />
                                  </div>
                                  <h3 className="font-medium text-gray-900 mb-2">{perk.name}</h3>
                                  <p className="text-sm text-gray-600">{perk.description}</p>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                    
                    {/* Quick Actions */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Zap className="w-5 h-5" />
                          Quick Actions
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <Button variant="outline" asChild className="h-auto p-4 flex-col items-start">
                            <Link to="/cart">
                              <Package className="w-5 h-5 mb-2 text-jamaica-green" />
                              <div className="text-left">
                                <div className="font-medium">Shop with Benefits</div>
                                <div className="text-sm text-gray-600">Use your member pricing</div>
                              </div>
                            </Link>
                          </Button>
                          
                          <Button variant="outline" asChild className="h-auto p-4 flex-col items-start">
                            <Link to="/rushd/pricing">
                              <Crown className="w-5 h-5 mb-2 text-jamaica-green" />
                              <div className="text-left">
                                <div className="font-medium">Learn More</div>
                                <div className="text-sm text-gray-600">Rush'd benefits & pricing</div>
                              </div>
                            </Link>
                          </Button>
                          
                          {isMember && (
                            <Button 
                              variant="outline" 
                              onClick={handleBillingPortal}
                              disabled={loadingBillingPortal}
                              className="h-auto p-4 flex-col items-start"
                            >
                              <CreditCard className="w-5 h-5 mb-2 text-jamaica-green" />
                              <div className="text-left">
                                <div className="font-medium">Billing Portal</div>
                                <div className="text-sm text-gray-600">Manage subscription</div>
                              </div>
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>
              
              {/* Notification Preferences Confirmation Dialog */}
              <Dialog open={showNotificationConfirmDialog} onOpenChange={setShowNotificationConfirmDialog}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Save Notification Preferences</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to save these notification preferences?
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-3 py-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <span className="text-sm font-medium">Order Updates</span>
                      <span className="text-sm text-gray-600">
                        {notificationPreferences.orderUpdates ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <span className="text-sm font-medium">Promotions & Discounts</span>
                      <span className="text-sm text-gray-600">
                        {notificationPreferences.promotions ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <span className="text-sm font-medium">Newsletters</span>
                      <span className="text-sm text-gray-600">
                        {notificationPreferences.newsletters ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={resetNotificationPreferences}
                      disabled={isUpdatingNotifications}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmSaveNotificationPreferences}
                      className="bg-jamaica-green hover:bg-jamaica-green/90"
                      disabled={isUpdatingNotifications || loading || !hasNotificationPreferencesChanged()}
                    >
                      {isUpdatingNotifications ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Preferences"
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Tabs>
          </div>
        </div>
      </div>
        </div>
      </main>
    </div>
    </>
  );
};

export default Settings;
